'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * global-error is de laatste vanglaag en blijft bewust dependency-light (inline
 * styles, geen @streep/ui-import) zodat hij ook rendert als de UI-bundle stuk
 * is. Daarom hier een eigen, compacte ChunkLoadError-detectie i.p.v. de gedeelde
 * useReloadOnChunkError-hook die de route-boundaries gebruiken: een chunk-fout
 * (een client met de vorige build na een deploy, of een module-import die
 * strandt in het iOS-resume-venster waarin netwerk en service worker nog niet
 * wakker zijn) herstellen we met een harde reload, met een sessionStorage-
 * debounce tegen reload-loops. Houd de patronen en de debounce-sleutel/-venster
 * hieronder in sync met isChunkLoadError / useReloadOnChunkError in @streep/ui.
 *
 * De weergave-beslissing valt al TIJDENS de render (zelfde aanpak als
 * useReloadOnChunkError) en kent drie standen: 'reload' (stille herstart met
 * page-reload), 'splash' (STREEP-splash zónder eigen reload) en 'kaart' (de
 * echte foutkaart). Stil herstart worden: (a) chunk-fouten, altijd; (b) elke
 * andere fout BINNEN het opstartvenster (~10s na page-load of app-resume).
 * Gaat de stille reload van zo'n niet-chunk-opstartfout NIET door (budget op
 * of offline), dan toont de splash: de meting van 2026-08-14 (Diemen) liet
 * zien dat Safari's eigen resume-herstel zo'n opstart-transient binnen ~1,5s
 * alsnog doorlaadt - een fouttekst is daar dus nooit op z'n plaats. De splash
 * is wel begrensd (OPSTART_SPLASH_MAX per reeks): een écht kapotte opstart
 * valt binnen enkele cycli terug op de kaart, en een fout midden in het
 * gebruik toont de kaart direct - echte fouten blijven altijd zichtbaar.
 * Chunk-fouten vallen buiten de splash: onderdrukt de chunk-debounce de
 * reload, dan hielp herladen net al niet en verschijnt direct de kaart.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

const RELOAD_TS_KEY = 'streep:chunk-reload-at' // zelfde key als useReloadOnChunkError (bewust geïnlined)
const RELOAD_DEBOUNCE_MS = 10_000

// Het opstartvenster: fouten binnen deze marge na een page-load of na het
// zichtbaar worden van de app (resume uit de iOS-app-switcher) zijn vrijwel
// altijd transiente opstartfoutjes (netwerk/service worker nog niet wakker).
const OPSTART_VENSTER_MS = 10_000
const VISIBLE_AT_KEY = 'streep:visible-at' // gestempeld door ClientTelemetryProvider (@streep/ui)
// Eigen reload-budget voor opstart-transients, LOS van de chunk-debounce
// (gedeeld budget zou een transient het chunk-zelfherstel laten onderdrukken).
// Bewust ruim: een echte opstartfout die elke cyclus terugkeert krijgt zo
// hooguit één stille poging per minuut en toont elke tussenliggende keer de
// echte foutkaart - een stille reload-loop is daarmee uitgesloten.
const OPSTART_HERSTEL_TS_KEY = 'streep:opstart-herstel-at'
const OPSTART_HERSTEL_DEBOUNCE_MS = 60_000

/** Valt "nu" binnen het opstartvenster van een verse load of een app-resume? */
function isOpstartVenster(): boolean {
  try {
    if (typeof window === 'undefined') return false
    // Verse page-load: performance.now() = ms sinds document-start.
    if (typeof performance?.now === 'function' && performance.now() < OPSTART_VENSTER_MS) {
      return true
    }
    // Resume uit de app-switcher: de provider stempelt elke zichtbaar-wording.
    // Klok-skew faalt dicht (negatief verschil = geen venster).
    const visibleAt = Number(window.sessionStorage.getItem(VISIBLE_AT_KEY) ?? '0')
    const geleden = Date.now() - visibleAt
    return visibleAt > 0 && geleden >= 0 && geleden < OPSTART_VENSTER_MS
  } catch {
    // laatste vanglaag: elke onverwachte fout hier = gewoon de foutkaart tonen
    return false
  }
}

/**
 * Classificeert de fout naar een VASTE categorie-key voor de telemetrie. Geeft
 * per constructie nooit message-tekst door: elke return is een literal (het
 * React-foutnummer is het enige afgeleide, en dat is een getal). Zo blijft de
 * ongeredigeerde beacon-route PII-vrij en zien we tóch welke transient-klasse
 * de opstartfoutjes veroorzaakt.
 */
type FoutKlasse =
  | 'chunk'
  | 'rsc-stream'
  | 'fetch-webkit'
  | 'fetch-chromium'
  | 'fetch-firefox'
  | 'fetch-netwerk'
  | 'offline'
  | 'onbekend'
  | `react-${number}`

function klasseVanFout(error: unknown): FoutKlasse {
  if (isChunkLoadError(error)) return 'chunk'
  if (!error || typeof error !== 'object') return 'onbekend'
  const message = ((error as { message?: string }).message ?? '').slice(0, 300)
  if (/^Connection closed/i.test(message)) return 'rsc-stream'
  if (/(?:^|: )Load failed$/.test(message)) return 'fetch-webkit'
  if (/(?:^|: )Failed to fetch$/.test(message)) return 'fetch-chromium'
  if (/NetworkError when attempting/i.test(message)) return 'fetch-firefox'
  if (/(?:^|: )network error$/i.test(message)) return 'fetch-netwerk'
  if (/appears to be offline/i.test(message)) return 'offline'
  const react = /Minified React error #(\d{1,4})(?!\d)/.exec(message)
  if (react) return `react-${Number(react[1])}`
  return 'onbekend'
}

/**
 * Stille-herstart-beslissing, éénmalig per fout genomen (zie de ref in het
 * component). Chunk-fouten (stale build na een deploy, gestrande module-import)
 * herstellen altijd stil; alle overige fouten ALLEEN binnen het opstartvenster:
 * een transient opstartfoutje hoort geen "Er ging iets mis" te flitsen, maar
 * een crash midden in het gebruik is een echte fout en toont direct de kaart.
 *
 * De debounce-timestamp wordt METEEN bij een positieve beslissing geschreven:
 * alleen een geslaagde write geeft recht op een auto-reload, dus een
 * aanhoudende (echte) opstartfout krijgt precies één stille poging en toont
 * daarna alsnog de foutkaart. Is sessionStorage geblokkeerd, dan valt de
 * beslissing negatief uit - zonder debounce-administratie is een reload-loop
 * anders niet uit te sluiten.
 */
function bepaalWillReload(error: unknown): boolean {
  if (typeof window === 'undefined') return false
  const chunk = isChunkLoadError(error)
  if (!chunk && !isOpstartVenster()) return false
  // Zonder netwerk kan een reload niets herstellen en kan hij de gebruiker op
  // de browser-offline-pagina laten stranden; beslis dan negatief. Binnen het
  // opstartvenster neemt de splash het over (herladen doet de gebruiker daar
  // zelf via de vangnet-knop zodra er weer verbinding is), daarbuiten de kaart.
  if (!chunk && typeof navigator !== 'undefined' && navigator.onLine === false) return false
  try {
    const key = chunk ? RELOAD_TS_KEY : OPSTART_HERSTEL_TS_KEY
    const venster = chunk ? RELOAD_DEBOUNCE_MS : OPSTART_HERSTEL_DEBOUNCE_MS
    const last = Number(window.sessionStorage.getItem(key) ?? '0')
    if (Date.now() - last < venster) return false
    window.sessionStorage.setItem(key, String(Date.now()))
    return true
  } catch {
    return false
  }
}

// Begrenzing van de opstart-splash (de "geen fouttekst bij openen"-stand):
// maximaal OPSTART_SPLASH_MAX splash-weergaves per reeks, daarna verschijnt
// de echte foutkaart alsnog. Een reeks reset na OPSTART_SPLASH_RESET_MS rust;
// fouten binnen OPSTART_SPLASH_INCIDENT_MS na elkaar (dezelfde kapotte
// page-run, zoals de gemeten react-310 + trailing TypeError ~0,7s later)
// tellen als één incident - bewust ruim onder de 4s-vangnetknop, zodat een
// handmatige herlaadpoging altijd meetelt.
const OPSTART_SPLASH_TELLER_KEY = 'streep:opstart-splash-teller'
const OPSTART_SPLASH_MAX = 2
const OPSTART_SPLASH_RESET_MS = 120_000
const OPSTART_SPLASH_INCIDENT_MS = 2_000

type Weergave = 'reload' | 'splash' | 'kaart'

/**
 * Mag een opstartvenster-fout waarvoor de stille reload niet doorging de
 * STREEP-splash tonen i.p.v. de foutkaart? Zelfde principe als de
 * reload-debounce: alleen een geslaagde sessionStorage-write geeft er recht
 * op. Zonder administratie (storage geblokkeerd) valt de beslissing negatief
 * uit - een onbegrensde splash is anders niet uit te sluiten.
 */
function magOpstartSplashTonen(): boolean {
  try {
    const nu = Date.now()
    let n = 0
    let at = 0
    const raw = window.sessionStorage.getItem(OPSTART_SPLASH_TELLER_KEY)
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
          const p = parsed as { n?: unknown; at?: unknown }
          if (typeof p.n === 'number' && Number.isFinite(p.n)) n = Math.max(0, Math.floor(p.n))
          if (typeof p.at === 'number' && Number.isFinite(p.at)) at = p.at
        }
      } catch {
        // Corrupte waarde = verse reeks; de write hieronder overschrijft 'm.
      }
    }
    // Klok-skew (at in de toekomst) valt dicht, net als isOpstartVenster:
    // liever één keer onterecht de kaart dan extra splashes op een zwevende klok.
    if (at > nu) return false
    // Fouten vlak na elkaar horen bij dezelfde kapotte page-run: splash
    // blijven tonen zonder de teller te verhogen. Dat `at` hier bewust NIET
    // wordt ververst is de rem - een gestage drup fouten kan het venster niet
    // eindeloos voor zich uit schuiven en valt na de reeks alsnog op de kaart.
    if (at > 0 && nu - at < OPSTART_SPLASH_INCIDENT_MS) return true
    if (nu - at > OPSTART_SPLASH_RESET_MS) n = 0
    if (n >= OPSTART_SPLASH_MAX) return false
    window.sessionStorage.setItem(OPSTART_SPLASH_TELLER_KEY, JSON.stringify({ n: n + 1, at: nu }))
    return true
  } catch {
    // Geen sessionStorage = geen begrenzing mogelijk -> de kaart.
    return false
  }
}

/**
 * De volledige weergave-beslissing, éénmalig per fout genomen. De volgorde is
 * betekenisvol: bepaalWillReload schrijft bij een positieve beslissing zelf
 * de reload-debounce; alleen als die negatief uitvalt komt de splash in
 * beeld, en uitsluitend binnen het opstartvenster.
 */
function bepaalWeergave(error: unknown): Weergave {
  if (bepaalWillReload(error)) return 'reload'
  // Chunk-fouten krijgen nooit de splash: onderdrukt de chunk-debounce de
  // reload, dan hielp herladen tien seconden geleden al niet (ontbrekend
  // asset, kapotte deploy) en is de kaart met chunk-referentie eerlijker dan
  // een eeuwige laadbalk.
  if (isChunkLoadError(error)) return 'kaart'
  if (isOpstartVenster() && magOpstartSplashTonen()) return 'splash'
  return 'kaart'
}

/**
 * Haalt het chunk-pad uit een chunk-foutmelding (alleen het /_next/static/...-
 * pad, geen host/query, lengte-geklemd). Geïnlinede kopie van chunkPadUitFout -
 * houd de regex in sync met use-reload-on-chunk-error.ts in @streep/ui.
 */
function chunkPadUitFout(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const message = ((error as { message?: string }).message ?? '').slice(0, 2000)
  const match = /\/_next\/static\/[\w@./()%[\]-]+?\.(?:m?js|css)/.exec(message)
  if (!match) return undefined
  const pad = match[0].slice(0, 200)
  return /\)\(/.test(pad) ? undefined : pad
}

/**
 * Best-effort event naar App Insights via navigator.sendBeacon, zodat deze
 * boundary niet langer een meet-blinde-vlek is (een reload of weggevallen
 * netwerk gooit de normale SDK-batch weg; global-error laadt de SDK sowieso
 * niet). Geïnlinede kopie van verstuurTelemetryBeacon - houd storage-key en
 * envelope-formaat in sync met packages/ui/src/lib/telemetry-beacon.ts. De
 * config staat in sessionStorage, daar neergezet door ClientTelemetryProvider.
 * PII: alleen opake properties (digest, error-naam, vlaggen, het via
 * chunkPadUitFout strikt geëxtraheerde chunk-pad); nooit vrije foutmeldingen
 * of URL's - deze route loopt niet door de telemetrie-redactie.
 */
function verstuurBeacon(eventName: string, properties: Record<string, string>): void {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return
    const raw = window.sessionStorage.getItem('streep:ai-beacon')
    if (!raw) return
    const config = JSON.parse(raw) as {
      iKey?: string
      ingestionEndpoint?: string
      role?: string
      appVersion?: string
    }
    if (!config.iKey || !config.ingestionEndpoint) return
    // Opake gebruikers-id (indien ingelogd) -> UserAuthenticatedId-kolom,
    // zelfde kolom als de SDK-events. Geklemd op een simpele identifier.
    const rawUser = window.sessionStorage.getItem('streep:ai-beacon-user')
    const authUserId = rawUser && /^[\w-]{1,64}$/.test(rawUser) ? rawUser : undefined
    const envelope = {
      name: `Microsoft.ApplicationInsights.${config.iKey.replace(/-/g, '')}.Event`,
      time: new Date().toISOString(),
      iKey: config.iKey,
      tags: {
        ...(config.role ? { 'ai.cloud.role': config.role } : {}),
        'ai.device.type': 'Browser',
        ...(config.appVersion ? { 'ai.application.ver': config.appVersion } : {}),
        ...(authUserId ? { 'ai.user.authUserId': authUserId } : {}),
        'ai.internal.sdkVersion': 'streep-beacon:1',
      },
      data: {
        baseType: 'EventData',
        baseData: { ver: 2, name: eventName, properties },
      },
    }
    navigator.sendBeacon(`${config.ingestionEndpoint}/v2/track`, JSON.stringify([envelope]))
  } catch {
    // beacon is best-effort; mag de foutafhandeling nooit verstoren
  }
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // De beslissing wordt per FOUT-identiteit vastgezet (niet per mount): klikt
  // de gebruiker op "Opnieuw proberen" en gooit de retry direct een chunk-fout,
  // dan hergebruikt React deze fiber en moet de nieuwe fout een verse
  // beslissing krijgen. De weergavekeuze is daarmee stabiel per fout - geen
  // flits van de foutkaart vóór de reload of splash. Zelfde patroon als
  // useReloadOnChunkError.
  const beslissing = useRef<{ error: unknown; weergave: Weergave } | null>(null)
  if (beslissing.current === null || beslissing.current.error !== error) {
    beslissing.current = { error, weergave: bepaalWeergave(error) }
  }
  const weergave = beslissing.current.weergave
  const [eventId, setEventId] = useState<string | null>(null)

  useEffect(() => {
    const klasse = klasseVanFout(error)
    // Welke chunk faalde (indien de melding het noemt) - onderscheidt eigen
    // lazy modules van Next-route-chunks in de telemetrie. Alleen voor
    // chunk-fouten: de ongeredigeerde beacon mag nooit een message-afgeleide
    // van willekeurige fouten dragen.
    const chunkPad = klasse === 'chunk' ? chunkPadUitFout(error) : undefined
    const errorName = /^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(error.name) ? error.name : 'Error'
    if (weergave === 'reload') {
      // De debounce-timestamp is al bij de beslissing geschreven. Ook de stil
      // geheelde gevallen worden volledig geteld en geclassificeerd - er
      // verdwijnt niets uit de telemetrie.
      if (klasse === 'chunk') {
        // Zelfde event-naam als de route-boundaries zodat de chunk-reload-churn
        // in de ops-rapportage deze gevallen meetelt.
        verstuurBeacon('chunk-reload', {
          boundary: 'global',
          via: 'beacon',
          ...(chunkPad ? { chunk: chunkPad } : {}),
        })
      } else {
        // Transient opstartfoutje: stil herstart binnen het opstartvenster.
        verstuurBeacon('global-error', {
          boundary: 'global',
          actie: 'auto-reload',
          klasse,
          errorName,
          ...(error.digest ? { digest: error.digest } : {}),
        })
      }
      window.location.reload()
      return
    }
    // Splash én kaart loggen naar de console: op een aangesloten device
    // blijft de fout ook in de splash-stand zichtbaar voor debugging.
    console.error(error)
    if (weergave === 'splash') {
      // Opstartvenster-fout zonder eigen reload (budget op of offline): de
      // splash staat en Safari's resume-herstel of de vangnet-knop doet de
      // volgende poging. opstart is hier per beslissing altijd '1'.
      verstuurBeacon('global-error', {
        boundary: 'global',
        actie: 'splash',
        klasse,
        opstart: '1',
        errorName,
        ...(error.digest ? { digest: error.digest } : {}),
      })
      return
    }
    setEventId(error.digest ?? null)
    // Echte fout (of uitgeputte splash-reeks): de kaart is zichtbaar. De
    // volledige exception kan hier niet betrouwbaar heen (geen SDK); klasse +
    // digest + naam is genoeg om het terug te vinden en te tellen. errorName
    // geklemd op een simpele identifier zodat er nooit vrije tekst in de
    // beacon lekt.
    verstuurBeacon('global-error', {
      boundary: 'global',
      actie: 'foutkaart',
      klasse,
      opstart: isOpstartVenster() ? '1' : '0',
      errorName,
      ...(klasse === 'chunk' ? { chunkFallback: '1' } : {}),
      ...(chunkPad ? { chunk: chunkPad } : {}),
      ...(error.digest ? { digest: error.digest } : {}),
    })
  }, [error, weergave])

  // Zelfherstel-splash: geen fouttekst maar het STREEP-merk met een vegende
  // "streep" als laadindicator, alsof de app gewoon opstart. Zowel voor de
  // reload-stand (wij herladen zelf) als de splash-stand (Safari's
  // resume-herstel of de vangnet-knop herlaadt). De vangnet-knop verschijnt
  // pas na 4s (puur CSS), voor het geval automatisch herstel uitblijft.
  if (weergave !== 'kaart') {
    return (
      <html lang="nl">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Laden - STREEP Portal</title>
          <style>{`
            @keyframes streepPuls { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
            @keyframes streepVeeg { 0% { transform: translateX(-100%); } 100% { transform: translateX(230%); } }
            @keyframes streepToon { to { opacity: 1; pointer-events: auto; } }
          `}</style>
        </head>
        <body
          style={{
            margin: 0,
            padding: 0,
            backgroundColor: '#0A0A0A',
            color: '#F0EDE8',
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            {/* STREEP-wordmark (inline kopie van LogoLight uit @streep/ui -
                dit bestand mag niet van die bundle afhangen). */}
            <svg
              width={190}
              height={53}
              viewBox="0 0 1089.7 305.1"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="STREEP"
              role="img"
              style={{ animation: 'streepPuls 2s ease-in-out infinite' }}
            >
              <polygon points="0 305.1 0 173 428.3 0 428.3 132 0 305.1" fill="#BEF264" />
              <path
                fill="#f3f3f3"
                d="M707.5,263.6c7.5-1.9,13-5.7,16.5-11.5,3.5-5.8,5.2-13.9,5.2-24.3v-17c0-13.2-3-22.8-9-28.8-6-6-15.6-8.9-28.7-8.9h-79.2v132h34v-39.6h26.4l21.7,39.6h36.8l-23.6-41.5h0ZM695.2,227.7c0,7.5-3.8,11.3-11.3,11.3h-37.7v-39.6h37.7c7.5,0,11.3,3.8,11.3,11.3v17Z"
              />
              <path
                fill="#f3f3f3"
                d="M846.5,199.4v-26.4h-104.7v132h104.7v-26.4h-70.7v-27.3h55.7v-26.4h-55.7v-25.5h70.7Z"
              />
              <path
                fill="#f3f3f3"
                d="M962,199.4v-26.4h-104.7v132h104.7v-26.4h-70.7v-27.3h55.7v-26.4h-55.7v-25.5h70.7Z"
              />
              <path
                fill="#f3f3f3"
                d="M1080.7,182c-6-6-15.6-8.9-28.7-8.9h-79.3v132h34v-37.7h45.3c13.2,0,22.8-3,28.7-9,6-6,9-15.6,9-28.7v-18.9c0-13.2-3-22.8-9-28.8h0ZM1055.8,229.6c0,7.5-3.8,11.3-11.3,11.3h-37.8v-41.5h37.8c7.5,0,11.3,3.8,11.3,11.3v18.9Z"
              />
              <path
                fill="#f3f3f3"
                d="M477.5,233.5c5.8,5.8,8.7,14.8,8.7,27.2v6.7c0,13.2-3,22.8-8.9,28.8s-15.6,8.9-28.8,8.9h-77.3v-26.4h69.8c7.5,0,11.3-3.8,11.3-11.3v-14.2h-43.4c-13.2,0-22.8-3-28.7-8.9-6-6-9-15.6-9-28.8v-4.6c0-13.2,3-22.8,9-28.8,6-6,15.5-8.9,28.7-8.9h72.1v26.4h-64.5c-7.5,0-11.3,3.8-11.3,11.3v2.7c0,7.5,3.8,11.3,11.3,11.3h34c12.3,0,21.4,2.9,27.2,8.7h0Z"
              />
              <polygon
                fill="#f3f3f3"
                points="601.5 173 601.5 199.4 561.9 199.4 561.9 305.1 527.9 305.1 527.9 199.4 491.7 199.4 491.7 173 601.5 173"
              />
            </svg>

            {/* De laadindicator: een lime streep die over de balk veegt. */}
            <div
              style={{
                width: '190px',
                height: '3px',
                margin: '28px auto 0',
                borderRadius: '2px',
                backgroundColor: '#2D2D2D',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '45%',
                  height: '100%',
                  borderRadius: '2px',
                  backgroundColor: '#BEF264',
                  animation: 'streepVeeg 1.1s ease-in-out infinite',
                }}
              />
            </div>

            {/* Vangnet wanneer automatisch herstel uitblijft (eigen reload in
                de reload-stand, Safari's resume-herstel in de splash-stand);
                verschijnt pas na 4s zodat het eerste beeld een pure splash is. */}
            <button
              onClick={() => window.location.reload()}
              style={{
                opacity: 0,
                pointerEvents: 'none',
                animation: 'streepToon 0.4s ease-in 4s forwards',
                marginTop: '44px',
                backgroundColor: 'transparent',
                color: '#6B6B6B',
                border: '1px solid #2D2D2D',
                borderRadius: '6px',
                padding: '0.5rem 1.25rem',
                fontSize: '0.875rem',
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                cursor: 'pointer',
              }}
            >
              Nu opnieuw laden
            </button>
          </div>
        </body>
      </html>
    )
  }

  return (
    <html lang="nl">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Er ging iets mis - STREEP Portal</title>
      </head>
      <body
        style={{
          margin: 0,
          padding: 0,
          backgroundColor: '#0A0A0A',
          color: '#F0EDE8',
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            maxWidth: '480px',
            width: '100%',
          }}
        >
          {/* Icon */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#2D2D2D',
              marginBottom: '1.5rem',
            }}
          >
            <span
              style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#BEF264',
                lineHeight: 1,
              }}
            >
              !
            </span>
          </div>

          {/* Heading */}
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#F0EDE8',
              margin: '0 0 0.75rem 0',
            }}
          >
            Er ging iets mis
          </h1>

          {/* Description */}
          <p
            style={{
              fontSize: '0.9375rem',
              color: '#6B6B6B',
              margin: '0 0 1.5rem 0',
              lineHeight: '1.6',
            }}
          >
            Er is een onverwachte fout opgetreden. Probeer de pagina opnieuw te laden.
          </p>

          {/* FO-364 - fout-referentie (Next-digest), zelfde "ref:"-label als de
              overige foutpagina's. Opaque id, geen stack, geen PII. */}
          {eventId && (
            <p
              style={{
                fontSize: '0.75rem',
                color: '#6B6B6B',
                margin: '0 0 1.5rem 0',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
              }}
            >
              ref: {eventId}
            </p>
          )}

          {/* Reset button */}
          <button
            onClick={reset}
            style={{
              backgroundColor: '#BEF264',
              color: '#0A0A0A',
              border: 'none',
              borderRadius: '6px',
              padding: '0.625rem 1.5rem',
              fontSize: '0.9375rem',
              fontWeight: '600',
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              cursor: 'pointer',
            }}
          >
            Opnieuw proberen
          </button>
        </div>
      </body>
    </html>
  )
}
